import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/dist/query/react";
import { API_ENDPOINT } from "../../config";
import { CartOrder } from "../../interfaces";
import { toastMessage } from "../../utils/toastError";

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

export type RefundDetails = {
  refund_id:                    string;
  refund_date:                  string;
  refund_time:                  string; // "HH:mm"
  corresponding_invoice_number: string;
  item:                         string;
  reason:                       string;
  notes?:                       string | null;
};

export const PAYMENT_PURPOSE_OPTIONS = [
  { label: "Module Fee",    value: "MODULE_FEE" },
  { label: "Postal Fee",    value: "POSTAL_FEE" },
  { label: "Admission Fee", value: "ADMISSION_FEE" },
  { label: "Course Fee",    value: "COURSE_FEE" },
  { label: "Others",        value: "OTHERS" },
] as const;

export type PurposeValue = typeof PAYMENT_PURPOSE_OPTIONS[number]["value"];

export type EditOrderBody = {
  purpose?:        PurposeValue;
  payment_method?: string;
  notes?:          string | null;
  order_status?:   "PAID" | "PENDING" | "FAILED" | "REFUNDED";
  payment_date?:   string | null;
  refund_details?: RefundDetails | null;
};

export interface CreateAdminOrderPayload {
  student_id:     string;
  items: {
    batch_id:       string;
    price_snapshot: number;
    quantity?:      number;
    item_name?:     string;
    sku_items?: {
      sku_code:          string;
      quantity:          number;
      tracking_id?:      string | null;
      dispatch_date?:    string | null;
      delivery_partner?: string | null;
    }[];
  }[];
  purpose:         string;
  notes?:          string;
  payment_method?: string;
  order_status?:   "PAID" | "REFUNDED";
  payment_date?:   string;
  refund_details?: RefundDetails;
}

export interface CreateAdminOrderResponse {
  message:        string;
  order:          CartOrder;
  invoice_number: string;
  payment_id:     string;
}

export interface InvoiceUpdate {
  id:             string;
  invoice_number: string;
  description?:   string;
  amount:         number;
}

export interface FinalizeInvoicesPayload {
  updates: InvoiceUpdate[];
}

export type UpdateOrderItemPayload = {
  id:              string;
  item_name?:      string;
  price_snapshot?: number;
  quantity?:       number;
  description?:    string | null;
  due_date?:       string | null;
};

export type DeleteOrderPayload = {
  id:             string;
  restore_stock?: boolean;
};

// ── NEW ──────────────────────────────────────────────────────────────────────

export type OrderItemRow = {
  id:             string;
  item_name:      string;
  description:    string | null;
  price_snapshot: number;
  quantity:       number;
  due_date:       string | null;
  item_type:      string | null;
  added_by_admin: boolean;
  sku_items:      any[] | null;
  created_at:     string;
  updated_at:     string;
  batch_code:     string | null;
  course_code:    string | null;
  course_name:    string | null;
  batch: {
    id:          string;
    name:        string;
    batch_code:  string | null;
    course_code: string | null;
    start_date:  string | null;
    end_date:    string | null;
  } | null;
  order: {
    id:             string;
    receipt_no:     string | null;
    invoice_number: string | null;
    status:         string;
    amount:         number;
    payment_date:   string | null;
    payment_method: string | null;
  } | null;
  student: {
    id:                  string;
    name:                string | null;
    email:               string | null;
    mobile_number:       string | null;
    phone:               string | null;
    registration_number: string | null;
  } | null;
};

export type GetOrderItemsParams = Partial<{
  student_id:          string;
  student_name:        string;
  student_email:       string;
  student_phone:       string;
  registration_number: string;
  item_name:           string;
  batch_code:          string;
  course_code:         string;
  global_search:       string;
  added_by_admin:      "true" | "false";
  status:              string;
  payment_date_from:   string;
  payment_date_to:     string;
  skip:                number;
  take:                number;
  sort:                "created_at" | "updated_at" | "price_snapshot" | "item_name";
  orderby:             "ASC" | "DESC";
}>;

// ─────────────────────────────────────────────────────────────────────────────
// API
// ─────────────────────────────────────────────────────────────────────────────

export const cartOrderApi = createApi({
  reducerPath: "cartOrderApi",
  baseQuery: fetchBaseQuery({
    baseUrl: API_ENDPOINT + "/admin/cart-order",
    credentials: "include",
  }),
  tagTypes: ["CartOrders"],
  endpoints: (build) => ({

    getCartOrders: build.query<{ orders: CartOrder[]; total: number }, Partial<any>>({
      query: (params) => ({
        url: "/all",
        method: "GET",
        params,
      }),
      providesTags: (result) =>
        result
          ? [
              ...result.orders.map((order) => ({ type: "CartOrders" as const, id: order.id })),
              { type: "CartOrders", id: "LIST" },
            ]
          : [{ type: "CartOrders", id: "LIST" }],
      transformErrorResponse(baseQueryReturnValue) {
        toastMessage("error", baseQueryReturnValue.data);
      },
    }),

    // ── NEW: flat paginated order line items ─────────────────────────
    getOrderItems: build.query<
      { items: OrderItemRow[]; total: number; skip: number; take: number },
      GetOrderItemsParams
    >({
      query: (params) => ({
        url: "/items",
        method: "GET",
        params,
      }),
      providesTags: [{ type: "CartOrders", id: "LIST" }],
      transformErrorResponse(baseQueryReturnValue) {
        toastMessage("error", baseQueryReturnValue.data);
      },
    }),

    getCartOrderDetails: build.query<CartOrder, string>({
      query: (id) => ({
        url: "/details/" + id,
        method: "GET",
      }),
      transformResponse: (response: any): CartOrder => {
        return response?.order ?? response?.orders?.[0] ?? response;
      },
      providesTags: (result, error, id) => [{ type: "CartOrders", id }],
      transformErrorResponse(baseQueryReturnValue) {
        toastMessage("error", baseQueryReturnValue.data);
      },
    }),

    createAdminOrder: build.mutation<CreateAdminOrderResponse, CreateAdminOrderPayload>({
      query: (body) => ({
        url: "/create-order",
        method: "POST",
        body,
      }),
      invalidatesTags: [{ type: "CartOrders", id: "LIST" }],
      transformErrorResponse(baseQueryReturnValue) {
        toastMessage("error", baseQueryReturnValue.data);
      },
    }),

    finalizeInvoices: build.mutation<{ message: string }, FinalizeInvoicesPayload>({
      query: (body) => ({
        url: "/finalize-invoices",
        method: "POST",
        body,
      }),
      invalidatesTags: [{ type: "CartOrders", id: "LIST" }],
      transformErrorResponse(baseQueryReturnValue) {
        toastMessage("error", baseQueryReturnValue.data);
      },
    }),

    deleteOrder: build.mutation<{ message: string; restore_stock: boolean }, DeleteOrderPayload>({
      query: ({ id, restore_stock }) => ({
        url: `/${id}${restore_stock ? "?restore_stock=true" : ""}`,
        method: "DELETE",
      }),
      invalidatesTags: [{ type: "CartOrders", id: "LIST" }],
      transformErrorResponse(baseQueryReturnValue) {
        toastMessage("error", baseQueryReturnValue.data);
      },
    }),

    deleteOrderItem: build.mutation<{ message: string; order: CartOrder }, string>({
      query: (itemId) => ({
        url: "/item/" + itemId,
        method: "DELETE",
      }),
      invalidatesTags: [{ type: "CartOrders", id: "LIST" }],
      transformErrorResponse(baseQueryReturnValue) {
        toastMessage("error", baseQueryReturnValue.data);
      },
    }),

    updateOrderItem: build.mutation<{ message: string; order: CartOrder }, UpdateOrderItemPayload>({
      query: ({ id, ...body }) => ({
        url: "/item/" + id,
        method: "PATCH",
        body,
      }),
      invalidatesTags: [{ type: "CartOrders", id: "LIST" }],
      transformErrorResponse(baseQueryReturnValue) {
        toastMessage("error", baseQueryReturnValue.data);
      },
    }),

    editOrder: build.mutation<{ message: string; order: CartOrder }, { id: string } & EditOrderBody>({
      query: ({ id, ...body }) => ({
        url: `/${id}`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: "CartOrders", id },
        { type: "CartOrders", id: "LIST" },
      ],
      transformErrorResponse(baseQueryReturnValue) {
        toastMessage("error", baseQueryReturnValue.data);
      },
    }),

  }),
});

export const {
  useGetCartOrdersQuery,
  useLazyGetCartOrdersQuery,
  useGetOrderItemsQuery,
  useLazyGetOrderItemsQuery,
  useGetCartOrderDetailsQuery,
  useCreateAdminOrderMutation,
  useFinalizeInvoicesMutation,
  useDeleteOrderMutation,
  useDeleteOrderItemMutation,
  useUpdateOrderItemMutation,
  useEditOrderMutation,
} = cartOrderApi;